import { Fragment } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import LeLiveHeading from './LeLiveHeading'
import Divider from 'LuxKit/Divider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LeLiveHighlightCard from './LeLiveHighlightCard'
import LeLiveGetTicketsButton from './LeLiveGetTicketsButton'
import type { HighlightCardData } from './LeLiveHighlightCard'

interface Props {
  hasRegistration: boolean
}

function LeLiveHighlights({ hasRegistration }: Props) {
  const intl = useIntl()

  const highlights: Array<HighlightCardData> = [
    {
      id: 'highlight-marketplace',
      imageId: 'boftenzn8euv3cz2iz1g',
      title: intl.formatMessage({
        id: 'ACg69fAw5MmtRzJUVu2R',
        defaultMessage: 'A global travel marketplace',
        description: 'LE Live highlight card title for the travel marketplace section',
      }),
      body: intl.formatMessage({
        id: 'GEQSBwpY4I-AFdYONJx3',
        defaultMessage: 'Meet 50+ premium travel partners including hotels, airlines, cruise lines and tourism boards showcasing their best offers.',
        description: 'LE Live highlight card body for the travel marketplace section',
      }),
      presentedBy: 'PassportCard',
      presentedByLogoId: 'jnlxnrbgk10uiyhryeus',
      presentedByLogoHeight: 48,
    },
    {
      id: 'highlight-panels',
      imageId: 'zpl4oigz0cgfvwoqgcti',
      title: intl.formatMessage({
        id: 'CBj1NKa2udjtE0mJ76RI',
        defaultMessage: 'Live panels & guest speakers',
        description: 'LE Live highlight card title for the panels and speakers section',
      }),
      body: intl.formatMessage({
        id: 'w7cy9qlS_3O16EOpjtFC',
        defaultMessage: 'Hear insider tips, emerging trends, and stories from travel industry leaders, destination experts, and celebrity guests.',
        description: 'LE Live highlight card body for the panels and speakers section',
      }),
      presentedBy: 'Mastercard',
      presentedByLogoId: '9sz94m8h9agkc5dm9jp',
      presentedByLogoHeight: 48,
    },
    {
      id: 'highlight-offers',
      imageId: 'b3b1oc8djrym8blx6swk',
      title: intl.formatMessage({
        id: 'VTuDCT_T2kXhU0GB9rAu',
        defaultMessage: 'Exclusive event-only offers',
        description: 'LE Live highlight card title for the exclusive offers section',
      }),
      body: intl.formatMessage({
        id: 'aIH-uRdvmor3fAdCAZ8a',
        defaultMessage: 'Unlock and book limited-time offers only available at Luxury Escapes Live.',
        description: 'LE Live highlight card body for the exclusive offers section',
      }),
    },
    {
      id: 'highlight-sessions',
      imageId: '1gbsiveq9mos6bw92b5',
      title: intl.formatMessage({
        id: 'UIQti7PU6iyUBeujcMK6',
        defaultMessage: 'One-on-one expert sessions',
        description: 'LE Live highlight card title for the expert sessions section',
      }),
      body: intl.formatMessage({
        id: 'zqfDqGKfyjKArk2InWhn',
        defaultMessage: 'Chat face-to-face with Luxury Escapes travel specialists to access and bespoke recommendations for your next trip.',
        description: 'LE Live highlight card body for the expert sessions section',
      }),
    },
  ]

  return (
    <Sheet padding="40 20" desktopPadding="64 0" id="highlights">
      <LayoutContainer>
        <Group direction="vertical" gap={40} horizontalAlign="center">
          <Group direction="vertical" gap={8} horizontalAlign="center">
            <LeLiveHeading variant="heading1alt" as="h2" align="center">
              <FormattedMessage
                id="KvdE5NDpSRSkwj4qX6Gi"
                defaultMessage="Melbourne’s <em>unmissable</em> travel event"
                description="Highlights section heading with emphasis on unmissable"
                values={{
                  em: (chunks) => <em>{chunks}</em>,
                }}
              />
            </LeLiveHeading>
            <BodyText variant="medium" colour="light-foreground-alt" align="center">
              {intl.formatMessage({
                id: '5PbuaPXUwj2ScS7XElAy',
                defaultMessage: 'Enjoy an immersive, experience-led event where inspiration, expertise and exclusive offers come together.',
                description: 'Highlights section subtitle describing the event experience',
              })}
            </BodyText>
          </Group>

          <Group
            direction="vertical"
            desktopDirection="horizontal"
            desktopHorizontalAlign="stretch"
            gap={16}
            desktopGap={24}
          >
            {highlights.map((highlight, index) => (
              <Fragment key={highlight.id}>
                {index > 0 && (
                  <CSSBreakpoint max="tablet">
                    <Divider kind="primary" />
                  </CSSBreakpoint>
                )}
                <LeLiveHighlightCard data={highlight} />
              </Fragment>
            ))}
          </Group>

          <LeLiveGetTicketsButton hidden={hasRegistration} />
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LeLiveHighlights
